/**
 * Amazon Ads — DSP AUDIENCE Runtime Schema Introspection (Story 19.16.1, Task 3)
 *
 * THE single highest-impact code-review gate of Story 19.16.1.
 *
 * Why runtime introspection (instead of vendoring a schema):
 *   - Amazon's `audience` report-type docs page returns page-skeleton-only via
 *     WebFetch (verified May 2026).
 *   - Funnel.io's published documentation explicitly OMITS AUDIENCE from its
 *     supported report types (only Campaign / Geography / Inventory / Product).
 *   - Singer.io's tap-amazon-ads-dsp claims AUDIENCE support but its README does
 *     NOT publish the column list.
 *   - Code3 (May 2026) publishes 7 metrics + 2 audience-overlap dimensions as
 *     the production agency-facing schema — adopted as `FLOOR_METRICS` /
 *     `FLOOR_DIMENSIONS`.
 *
 * Runtime introspection persists the Amazon-returned schema per tenant +
 * integration in KV with a 7-day TTL. On TTL expiry the helper re-introspects
 * and emits a drift-comparison log so operators see schema changes immediately.
 *
 * Defensive layers (Round 3 Vulns 1 + 2 + Round 5 First Principles):
 *   1. **Code3-floor fallback** — Amazon returning `[]` falls back to the
 *      production-confirmed 9-column floor; emits
 *      `amazon_ads_dsp_audience_introspection_empty_using_fallback` log.
 *   2. **Cross-tenant cache key isolation** — KV key MUST include `tenantId`;
 *      tenantId-empty calls throw at the helper boundary.
 *   3. **XSS-style column-name injection defense** — every observed column
 *      passes through `/^[a-zA-Z][a-zA-Z0-9_]*$/`; rejected names emit
 *      `amazon_ads_dsp_audience_schema_invalid_column_name` log.
 *   4. **Drift detection on TTL re-introspection** — when prev + new schemas
 *      diverge, emits `amazon_ads_dsp_audience_schema_drift_detected` log.
 *
 * The helper is PURE w.r.t. IO: KV is injected; logging goes through
 * `console.log(JSON.stringify(...))` so Logpush + Grafana parse it directly.
 */

import type { AmazonAdsKvApi } from "./shared/report-orchestration";

// ============================================================================
// Constants — Code3-confirmed-floor (compile-time-frozen `as const`)
// ============================================================================

/**
 * Story 19.16.1 AC #8 — Code3-confirmed-floor metrics. These 7 columns are
 * GUARANTEED to be in any AUDIENCE response per Code3 May 2026 production
 * documentation. Used as fallback when Amazon's first-fetch returns `[]`.
 *
 * **Frozen — never hot-loaded from KV/D1 (Round 3 Vuln 3 defense).**
 */
export const AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS = [
  "spend",
  "impressions",
  "clicks",
  "brandedSearches",
  "detailPageViews",
  "addToCart",
  "purchases",
] as const;

/**
 * Story 19.16.1 AC #8 — Code3-confirmed-floor dimensions. `audienceSize` is an
 * enum of {"SMALL", "MEDIUM", "LARGE"} per Amazon Brand Stores audience-overlap
 * docs; `affinityIndex` is a numeric value > 0 (≤ 1 = no affinity, > 1 = positive).
 */
export const AMAZON_ADS_DSP_AUDIENCE_FLOOR_DIMENSIONS = [
  "audienceSize",
  "affinityIndex",
] as const;

export type AudienceFloorMetricName =
  (typeof AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS)[number];
export type AudienceFloorDimensionName =
  (typeof AMAZON_ADS_DSP_AUDIENCE_FLOOR_DIMENSIONS)[number];

/**
 * Story 19.16.1 AC #8 / Round 3 Vuln 1 — XSS-style column-name injection
 * defense. ASCII-letter-leading + ASCII-alphanumeric-or-underscore tail; this
 * matches Amazon's documented column-name shape and rejects every malicious
 * variant (HTML tags, JS protocol, control characters, etc.).
 */
export const AMAZON_ADS_DSP_COLUMN_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** 7-day TTL per AC #1 / #8. */
export const AMAZON_ADS_DSP_INTROSPECTED_SCHEMA_TTL_SEC = 7 * 24 * 3600;

/**
 * Returns the per-tenant cache key. **TenantId is required and validated
 * non-empty** — Round 3 Vuln 2 cross-tenant cache isolation invariant.
 */
export function buildAudienceSchemaCacheKey(args: {
  readonly tenantId: string;
  readonly integrationId: string;
}): string {
  if (!args.tenantId || typeof args.tenantId !== "string") {
    throw new Error(
      "buildAudienceSchemaCacheKey: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (!args.integrationId || typeof args.integrationId !== "string") {
    throw new Error("buildAudienceSchemaCacheKey: integrationId is required");
  }
  return `amazon_ads_dsp_introspected_schema:${args.tenantId}:${args.integrationId}:DSP_AUDIENCE`;
}

// ============================================================================
// Introspection helpers
// ============================================================================

export interface IntrospectAudienceSchemaArgs {
  readonly kv: AmazonAdsKvApi;
  readonly tenantId: string;
  readonly integrationId: string;
  /** Columns observed in Amazon's first-fetch response. */
  readonly observedColumns: ReadonlyArray<string>;
}

export interface IntrospectAudienceSchemaResult {
  readonly persisted: ReadonlyArray<string>;
  readonly rejected: ReadonlyArray<string>;
}

/**
 * Story 19.16.1 AC #8 / Task 3.4 — first-fetch introspection. Validates each
 * observed column against the column-name regex; persists clean column-names
 * to KV; returns the persisted/rejected split.
 *
 * Empty observed columns trigger the Code3-floor fallback log; the fallback
 * itself is applied by the caller (we don't write a partial schema to KV).
 */
export async function introspectAudienceSchema(
  args: IntrospectAudienceSchemaArgs
): Promise<IntrospectAudienceSchemaResult> {
  // Round 3 Vuln 2 — validate tenantId/integrationId BEFORE any log emission
  // so a misconfigured caller cannot leak per-tenant log payloads with empty
  // tenantId values. Mirrors `buildAudienceSchemaCacheKey` invariant at the
  // entry-point.
  if (!args.tenantId || typeof args.tenantId !== "string") {
    throw new Error(
      "introspectAudienceSchema: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (!args.integrationId || typeof args.integrationId !== "string") {
    throw new Error(
      "introspectAudienceSchema: integrationId is required"
    );
  }

  const persisted: string[] = [];
  const rejected: string[] = [];

  for (const col of args.observedColumns) {
    if (typeof col !== "string") {
      rejected.push(String(col));
      continue;
    }
    if (AMAZON_ADS_DSP_COLUMN_NAME_REGEX.test(col)) {
      persisted.push(col);
    } else {
      rejected.push(col);
    }
  }

  if (rejected.length > 0) {
    logEvent("amazon_ads_dsp_audience_schema_invalid_column_name", {
      tenantId: args.tenantId,
      integrationId: args.integrationId,
      rejected,
    });
  }

  if (persisted.length === 0) {
    logEvent("amazon_ads_dsp_audience_introspection_empty_using_fallback", {
      tenantId: args.tenantId,
      integrationId: args.integrationId,
    });
    return { persisted, rejected };
  }

  const key = buildAudienceSchemaCacheKey({
    tenantId: args.tenantId,
    integrationId: args.integrationId,
  });
  await args.kv.put(
    key,
    JSON.stringify(persisted),
    AMAZON_ADS_DSP_INTROSPECTED_SCHEMA_TTL_SEC
  );
  logEvent("amazon_ads_dsp_introspected_schema_persisted", {
    tenantId: args.tenantId,
    integrationId: args.integrationId,
    reportTypeId: "DSP_AUDIENCE",
    columns: persisted,
  });
  return { persisted, rejected };
}

/**
 * Story 19.16.1 Task 3.5 — load previously-introspected schema from KV.
 * Returns `null` when the schema has not been introspected yet (caller falls
 * back to the Code3 floor). Returns `null` on parse errors so a corrupted KV
 * value never crashes the fetch path.
 */
export async function loadIntrospectedAudienceSchema(args: {
  readonly kv: AmazonAdsKvApi;
  readonly tenantId: string;
  readonly integrationId: string;
}): Promise<ReadonlyArray<string> | null> {
  const key = buildAudienceSchemaCacheKey({
    tenantId: args.tenantId,
    integrationId: args.integrationId,
  });
  const raw = await args.kv.get(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const cleaned = parsed.filter(
      (c): c is string =>
        typeof c === "string" && AMAZON_ADS_DSP_COLUMN_NAME_REGEX.test(c)
    );
    return cleaned;
  } catch {
    return null;
  }
}

export interface AudienceSchemaDrift {
  readonly added: ReadonlyArray<string>;
  readonly removed: ReadonlyArray<string>;
}

/**
 * Story 19.16.1 Task 3.6 — drift detection on TTL re-introspection. Emits
 * `amazon_ads_dsp_audience_schema_drift_detected` log when prev + new differ
 * so operators see schema additions on the 7-day cycle.
 */
export function compareAudienceSchemaForDrift(
  prevColumns: ReadonlyArray<string>,
  newColumns: ReadonlyArray<string>
): AudienceSchemaDrift {
  const prev = new Set(prevColumns);
  const next = new Set(newColumns);
  const added = newColumns.filter((c) => !prev.has(c));
  const removed = prevColumns.filter((c) => !next.has(c));
  if (added.length > 0 || removed.length > 0) {
    logEvent("amazon_ads_dsp_audience_schema_drift_detected", {
      added,
      removed,
    });
  }
  return { added, removed };
}

/**
 * Story 19.16.1 AC #8 — AUDIENCE-specific runtime invariants. `audienceSize`
 * MUST be one of `{SMALL, MEDIUM, LARGE}` enum; `affinityIndex` MUST be a
 * finite positive number. Out-of-range values surface as `_malformed`.
 */
export const AMAZON_ADS_DSP_AUDIENCE_SIZE_ENUM = ["SMALL", "MEDIUM", "LARGE"] as const;
export type AudienceSizeEnumValue =
  (typeof AMAZON_ADS_DSP_AUDIENCE_SIZE_ENUM)[number];

export function isValidAudienceSize(value: unknown): value is AudienceSizeEnumValue {
  return (
    typeof value === "string" &&
    (AMAZON_ADS_DSP_AUDIENCE_SIZE_ENUM as readonly string[]).includes(value)
  );
}

export function isValidAffinityIndex(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

// ============================================================================
// Internal logging helper
// ============================================================================

function logEvent(event: string, details: Record<string, unknown>): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
